// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Tooltip } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import {
  AppIcon,
  CellData,
  DeleteButton,
  WatchlistToggleButton,
  Table,
} from '@shared/ui-kit/components';
import { OperationProductCell } from './operation-product-cell';
import { TradeAccountSelect } from './trade-account-select';
import { type ColumnMethods } from '@advisor-app/features/trades';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { type TradeDetails } from '@shared/data-access/interfaces/redux/trades';
import { numberToFixed } from '@shared/ui-kit/utils';
import { classNames } from '@shared/ui-kit/utils/classnames';
import {
  OperationType,
  normalizeOperationType,
} from '@shared/data-access/interfaces/operation-type';
import type { TFunction } from 'i18next';
import { isNull, isNullOrUndefined } from '@shared/utils';

type OperationTranslationKey = 'common.buy' | 'common.sell' | 'common.transfer';

const columnHelper = createColumnHelper<TradeDetails>();

const iconConfig = {
  [OperationType.BUY]: { icon: 'Trader', color: 'success' },
  [OperationType.SELL]: { icon: 'TrendingDown', color: 'danger' },
  [OperationType.TRANSFER]: { icon: 'ArrowLeftRight', color: 'main' },
} as const;

const isInvalidProposalAssetTrade = (trade: TradeDetails): boolean => {
  const normalizedOperation = normalizeOperationType(trade.operation);

  if (
    normalizedOperation !== OperationType.BUY &&
    normalizedOperation !== OperationType.TRANSFER
  ) {
    return false;
  }

  const assetDetails =
    normalizedOperation === OperationType.TRANSFER
      ? trade.to_asset_details
      : trade.columnName;

  if (!['fund', 'mutual_fund'].includes(assetDetails?.asset_type ?? '')) {
    return false;
  }

  const dfiUrl = assetDetails?.dfi_url;

  return isNullOrUndefined(dfiUrl) || dfiUrl.trim() === '';
};

// Helper function to get from/to ticker values based on operation type
// Reads directly from stored trade data
const getTickerValues = (trade: TradeDetails) => {
  const normalizedOperation = normalizeOperationType(trade.operation);
  switch (normalizedOperation) {
    case OperationType.BUY:
      return {
        from_ticker: trade.currency || null,
        to_ticker: trade.ticker,
      };
    case OperationType.SELL:
      return {
        from_ticker: trade.ticker,
        to_ticker: trade.currency || null,
      };
    case OperationType.TRANSFER:
      return {
        from_ticker: trade.ticker,
        to_ticker: trade.transfer_target || null,
      };
    default:
      return { from_ticker: null, to_ticker: null };
  }
};

// Helper function to get from/to ISIN values based on operation type
// Reads directly from stored trade data
const getIsinValues = (trade: TradeDetails) => {
  const normalizedOperation = normalizeOperationType(trade.operation);
  const tradeAny = trade as TradeDetails & {
    to_isin?: string;
  };

  switch (normalizedOperation) {
    case OperationType.BUY:
      return {
        from_isin: trade.currency || null,
        to_isin: trade.isin ?? null,
      };
    case OperationType.SELL:
      return {
        from_isin: trade.isin ?? null,
        to_isin: trade.currency || null,
      };
    case OperationType.TRANSFER:
      return {
        from_isin: trade.isin ?? null,
        to_isin: tradeAny.to_isin ?? null,
      };
    default:
      return { from_isin: null, to_isin: null };
  }
};
// Helper function to map operation types to their corresponding display labels
// BUY, SELL and TRANSFER operations are mapped to localized, user-friendly names
export const getOperationLabel = (
  operation: OperationType,
): OperationTranslationKey => {
  switch (operation) {
    case 'BUY':
      return 'common.buy';
    case 'SELL':
      return 'common.sell';
    case 'TRANSFER':
      return 'common.transfer';
    default:
      return 'common.buy';
  }
};

export const renderColumnsTrader = (columnMethods: ColumnMethods, t: TFunction) => {
  const {
    tickerOperation: handleDeleteTrade,
    onAccountChange,
    advisedIbans,
  } = columnMethods;

  return [
    columnHelper.accessor('operation', {
      header: t('common.operation'),
      cell: ({ getValue }) => {
        const operation = getValue();
        const normalizedOperation = normalizeOperationType(operation);
        const config =
          iconConfig[normalizedOperation as keyof typeof iconConfig] ??
          iconConfig[OperationType.SELL];
        const { icon, color } = config;
        return (
          <div className="flex items-center justify-center gap-x-2">
            <AppIcon icon={icon} color={color} className="inline-block" size="lg" />
            <CellData
              value={t(getOperationLabel(normalizedOperation))}
              format="capitalized"
            />
          </div>
        );
      },
    }),

    // From Product (Ticker/ISIN in 2 lines)
    columnHelper.accessor('operation', {
      id: 'from_product',
      header: t('common.from-product', { ns: 'common' }),
      minSize: 180,
      cell: ({ row }) => {
        const { from_ticker, from_isin } = {
          ...getTickerValues(row.original),
          ...getIsinValues(row.original),
        };
        const trade = row.original;

        // For BUY operations: show "Cash Account" above and currency below
        const normalizedOperation = normalizeOperationType(trade.operation);
        if (normalizedOperation === OperationType.BUY) {
          return (
            <OperationProductCell
              cashTitle={t('trader.accounts.cash', { ns: 'components' })}
              cashSubtitle={trade.currency}
            />
          );
        }

        if (
          [OperationType.SELL, OperationType.TRANSFER].includes(
            normalizedOperation,
          ) &&
          from_ticker &&
          from_ticker !== trade.currency
        ) {
          return (
            <OperationProductCell
              name={trade.columnName?.name ?? trade.name}
              ticker={from_ticker}
              isin={from_isin}
              assetType={trade.columnName?.asset_type}
              logoUrl={trade.columnName?.url_logo}
              url={trade.columnName?.url_morningstar}
            />
          );
        }

        if (!from_ticker && !from_isin) {
          return <span>-</span>;
        }

        return (
          <OperationProductCell
            name={trade.columnName?.name ?? trade.name}
            ticker={from_ticker}
            isin={from_isin}
            assetType={trade.columnName?.asset_type}
            logoUrl={trade.columnName?.url_logo}
            url={trade.columnName?.url_morningstar}
          />
        );
      },
    }),

    // From Account (Institution/IBAN)
    columnHelper.accessor('operation', {
      id: 'from_account',
      header: t('common.from-account', { ns: 'common' }),
      minSize: 180,
      cell: ({ row }) => (
        <TradeAccountSelect
          iban={row.original.from_iban ?? row.original.account_id}
          side="from"
          rowIndex={row.index}
          advisedIbans={advisedIbans}
          onAccountChange={onAccountChange}
        />
      ),
    }),

    // Quantity
    columnHelper.accessor('contracts_number', {
      id: 'qty',
      header: t('common.qty', { ns: 'common' }),
      meta: { alignment: 'end' },
      cell: ({ row }) => {
        const qty = row.original.contracts_number;
        const assetType = row.original.columnName?.asset_type;

        if (isNull(qty)) {
          return <span>-</span>;
        }

        const decimals = assetType === 'stock' ? 0 : 2;

        return <span>{qty.toFixed(decimals)}</span>;
      },
    }),

    // Price
    columnHelper.accessor(row => row.local_currency_values?.price, {
      id: 'price',
      header: t('common.price', { ns: 'common' }),
      meta: { alignment: 'end' },
      cell: ({ getValue, row }) => {
        const price = getValue() as number;
        const currency = row.original.local_currency_values?.currency;
        const hasPrice = numberToFixed(price, 5);

        if (!hasPrice) {
          return <span>-</span>;
        }

        return (
          <span className="!money-display">
            {formatToMoney(price, currency, undefined, 2)}
          </span>
        );
      },
    }),

    // Estimated Value
    columnHelper.accessor(row => row.local_currency_values?.expected_volume, {
      header: t('common.amount', { ns: 'common' }),
      id: 'amount',
      meta: { alignment: 'end' },
      cell: ({ row }) => {
        const trade = row.original;
        const value = trade.local_currency_values?.expected_volume;
        const currency = trade.local_currency_values?.currency;

        // If both value and currency are missing, show only one "-"
        if (!value && !currency) {
          return <span>-</span>;
        }

        return (
          <div className="flex flex-col items-end">
            <span className="!money-display">
              {value
                ? formatToMoney(Math.round(value), currency, undefined, 0)
                : '-'}
            </span>
            {currency && <span className="text-sm">{currency}</span>}
          </div>
        );
      },
    }),

    // To Product (Ticker/ISIN in 2 lines)
    columnHelper.accessor('operation', {
      id: 'to_product',
      header: t('common.to-product', { ns: 'common' }),
      minSize: 180,
      enableHiding: false,
      cell: ({ row }) => {
        const { to_ticker, to_isin } = {
          ...getTickerValues(row.original),
          ...getIsinValues(row.original),
        };
        const trade = row.original;

        // For SELL operations: show "Cash Account" above and currency below
        const normalizedOperation = normalizeOperationType(trade.operation);
        if (normalizedOperation === OperationType.SELL) {
          return (
            <OperationProductCell
              cashTitle={t('trader.accounts.cash', { ns: 'components' })}
              cashSubtitle={trade.currency}
            />
          );
        }

        if (
          [OperationType.BUY, OperationType.TRANSFER].includes(
            normalizedOperation,
          ) &&
          to_ticker &&
          to_ticker !== trade.currency
        ) {
          const tradeAny = trade as TradeDetails & {
            to_asset_details?: {
              name?: string;
              asset_type?: string;
              url_logo?: string;
              url_morningstar?: string;
              dfi_url?: string | null;
            };
          };
          const toAssetDetails = tradeAny.to_asset_details;
          const assetDetails =
            normalizedOperation === OperationType.TRANSFER && toAssetDetails
              ? toAssetDetails
              : trade.columnName;

          return (
            <OperationProductCell
              name={assetDetails?.name ?? trade.name}
              ticker={to_ticker}
              isin={to_isin}
              assetType={assetDetails?.asset_type}
              logoUrl={assetDetails?.url_logo}
              url={assetDetails?.url_morningstar}
              watchlistButton={
                <WatchlistToggleButton
                  ticker={to_ticker}
                  assetType={assetDetails?.asset_type as string}
                />
              }
            />
          );
        }

        if (!to_ticker && !to_isin) {
          return <span>-</span>;
        }

        return (
          <OperationProductCell
            name={trade.columnName?.name ?? trade.name}
            ticker={to_ticker}
            isin={to_isin}
            assetType={trade.columnName?.asset_type}
            logoUrl={trade.columnName?.url_logo}
            url={trade.columnName?.url_morningstar}
          />
        );
      },
    }),

    // To Account (Institution/IBAN)
    columnHelper.accessor('operation', {
      id: 'to_account',
      header: t('common.to-account', { ns: 'common' }),
      minSize: 180,
      cell: ({ row }) => (
        <TradeAccountSelect
          iban={row.original.to_iban}
          side="to"
          rowIndex={row.index}
          advisedIbans={advisedIbans}
          onAccountChange={onAccountChange}
        />
      ),
    }),

    // Theoretical Investment (hidden by default)
    columnHelper.accessor('volume', {
      header: t('common.theoretical-investment', { ns: 'common' }),
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      cell: /* istanbul ignore next */ ({ row }) => {
        const trade = row.original;
        const localPrice = trade.local_currency_values?.price as number;
        const investment = localPrice ? trade.local_currency_values?.volume : 0;
        return (
          <span className="!money-display">
            {formatToMoney(
              investment!,
              row.original.local_currency_values?.currency,
            )}
          </span>
        );
      },
    }),

    // Delta (hidden by default)
    columnHelper.accessor(row => row.local_currency_values?.delta_volume, {
      id: 'delta',
      header: t('common.delta', { ns: 'common' }),
      meta: { alignment: 'end', initiallyHidden: true },
      cell: ({ getValue, row }) => (
        <span className="!money-display">
          {formatToMoney(
            getValue() as number,
            row.original.local_currency_values?.currency,
          )}
        </span>
      ),
    }),

    // Gain / Loss (can be hidden if needed)
    columnHelper.accessor('gain_loss', {
      header: t('common.gain-loss', { ns: 'common' }),
      id: 'gain_loss',
      meta: {
        initiallyHidden: true,
        alignment: 'end',
      },
      sortingFn: 'gainLoss',
      cell: ({ getValue, row }) => {
        const [value, loss] = getValue() as number[];
        const currency = row.original.local_currency_values?.currency;
        return (
          <span
            className={classNames({
              profit: value >= 0,
              loss: value < 0,
            })}
          >
            <Table.Data value={value} format="currency" currency={currency} />
            &nbsp;(
            <Table.Data value={loss} format="percentage" />)
          </span>
        );
      },
    }),

    // Options column
    columnHelper.accessor('operation', {
      id: 'options',
      enableSorting: false,
      meta: { showColumnVisibilitySelector: true, alignment: 'end' },
      header: '',
      cell: ({ getValue, row }) => {
        const isInvalidProposalAsset = isInvalidProposalAssetTrade(row.original);

        return (
          <div className="flex w-full items-center justify-end gap-2 whitespace-nowrap">
            {!numberToFixed(row.original.expected_price as number, 5) && (
              <Tooltip title={t('common.not-available')} placement="top">
                <div className="not-available line-clamp-1">
                  {t('common.not-available')}
                </div>
              </Tooltip>
            )}

            {isInvalidProposalAsset && (
              <Tooltip
                title={t('trader.invalid-proposal-asset-tooltip', {
                  ns: 'components',
                })}
                placement="top"
              >
                <div
                  data-testid="invalid-proposal-asset-warning"
                  className="invalid-proposal-asset inline-flex items-center gap-1 whitespace-nowrap !text-warning"
                >
                  <AppIcon icon="AlertTriangle" color="warning" size="md" />
                  {t('trader.invalid-proposal-asset-label', {
                    ns: 'components',
                  })}
                </div>
              </Tooltip>
            )}

            <DeleteButton
              data-testid="delete-trade"
              onClick={() =>
                handleDeleteTrade?.(row.original.ticker, getValue(), row.index)
              }
            />
          </div>
        );
      },
    }),
  ];
};
